/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { AirportManagementUnitResponse } from '../models/airport-management-unit-response';
import { CreateAirportManagementUnitRequest } from '../models/create-airport-management-unit-request';
import { GenericResponse } from '../models/generic-response';
import { GetAirportManagementUnitResponse } from '../models/get-airport-management-unit-response';
import { UpdateAirportManagementUnitRequest } from '../models/update-airport-management-unit-request';

/**
 * Airport Management Unit APIs
 */
@Injectable({ providedIn: 'root' })
export class AirportManagementUnitService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllAirportManagementUnits()` */
  static readonly GetAllAirportManagementUnitsPath =
    '/api/v1/airport-management/units';

  /**
   * Get list of all airport management units.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllAirportManagementUnits()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllAirportManagementUnits$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to following fields:
       *   - name
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetAirportManagementUnitResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AirportManagementUnitService.GetAllAirportManagementUnitsPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetAirportManagementUnitResponse>;
        })
      );
  }

  /**
   * Get list of all airport management units.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllAirportManagementUnits$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllAirportManagementUnits(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to following fields:
       *   - name
       */
      search?: string;
    },
    context?: HttpContext
  ): Observable<GetAirportManagementUnitResponse> {
    return this.getAllAirportManagementUnits$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<GetAirportManagementUnitResponse>
        ): GetAirportManagementUnitResponse => r.body
      )
    );
  }

  /** Path part for operation `createAirportManagementUnit()` */
  static readonly CreateAirportManagementUnitPath =
    '/api/v1/airport-management/units';

  /**
   * Create new airport management unit.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createAirportManagementUnit()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createAirportManagementUnit$Response(
    params: {
      body: CreateAirportManagementUnitRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<AirportManagementUnitResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AirportManagementUnitService.CreateAirportManagementUnitPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<AirportManagementUnitResponse>;
        })
      );
  }

  /**
   * Create new airport management unit.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createAirportManagementUnit$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createAirportManagementUnit(
    params: {
      body: CreateAirportManagementUnitRequest;
    },
    context?: HttpContext
  ): Observable<AirportManagementUnitResponse> {
    return this.createAirportManagementUnit$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<AirportManagementUnitResponse>
        ): AirportManagementUnitResponse => r.body
      )
    );
  }

  /** Path part for operation `getAirportManagementUnitById()` */
  static readonly GetAirportManagementUnitByIdPath =
    '/api/v1/airport-management/units/{id}';

  /**
   * Get airport management unit details using id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAirportManagementUnitById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAirportManagementUnitById$Response(
    params: {
      /**
       * The airport management unit's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<AirportManagementUnitResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AirportManagementUnitService.GetAirportManagementUnitByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<AirportManagementUnitResponse>;
        })
      );
  }

  /**
   * Get airport management unit details using id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAirportManagementUnitById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAirportManagementUnitById(
    params: {
      /**
       * The airport management unit's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<AirportManagementUnitResponse> {
    return this.getAirportManagementUnitById$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<AirportManagementUnitResponse>
        ): AirportManagementUnitResponse => r.body
      )
    );
  }

  /** Path part for operation `deleteAirportManagementUnitById()` */
  static readonly DeleteAirportManagementUnitByIdPath =
    '/api/v1/airport-management/units/{id}';

  /**
   * Delete airport management unit by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteAirportManagementUnitById()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteAirportManagementUnitById$Response(
    params: {
      /**
       * The airport management unit id that needs to be disabled
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AirportManagementUnitService.DeleteAirportManagementUnitByIdPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Delete airport management unit by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteAirportManagementUnitById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteAirportManagementUnitById(
    params: {
      /**
       * The airport management unit id that needs to be disabled
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.deleteAirportManagementUnitById$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateAirportManagementUnitById()` */
  static readonly UpdateAirportManagementUnitByIdPath =
    '/api/v1/airport-management/units/{id}';

  /**
   * Update airport management unit by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateAirportManagementUnitById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateAirportManagementUnitById$Response(
    params: {
      /**
       * The airport management unit's id that needs to be updated
       */
      id: number;
      body: UpdateAirportManagementUnitRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<AirportManagementUnitResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AirportManagementUnitService.UpdateAirportManagementUnitByIdPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<AirportManagementUnitResponse>;
        })
      );
  }

  /**
   * Update airport management unit by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateAirportManagementUnitById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateAirportManagementUnitById(
    params: {
      /**
       * The airport management unit's id that needs to be updated
       */
      id: number;
      body: UpdateAirportManagementUnitRequest;
    },
    context?: HttpContext
  ): Observable<AirportManagementUnitResponse> {
    return this.updateAirportManagementUnitById$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<AirportManagementUnitResponse>
        ): AirportManagementUnitResponse => r.body
      )
    );
  }
}
